<template>
  <KButton
    :label="label"
    class="icon-button"
    :class="[
      {
        'icon-button--small': size === 'small',
        'icon-button--medium': size === 'medium',
        'icon-button--large': size === 'large',
        'icon-button--disabled': disabled,
        'icon-button--hide-label': !label
      },
      buttonClass
    ]"
    :disabled="disabled"
    text
  >
    <template #icon>
      <KIcon :icon="icon" :size="iconSize" />
    </template>
  </KButton>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { KButton, KIcon } from "@k2united/frontend_components";

interface Props {
  /**
   * Button label
   */
  label?: string;

  /**
   * Font Awesome icon name
   */
  icon: string;

  /**
   * Sizes: small, medium, or large
   */
  size?: "small" | "medium" | "large";

  /**
   * Primary button with no background
   */
  primary?: boolean;

  /**
   * Secondary button with background
   */
  secondary?: boolean;

  /**
   * Disable the button
   */
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  label: undefined,
  size: "small",
  disabled: false,
  primary: true,
  secondary: false
});

const iconSize = computed<
  | "2xs"
  | "xs"
  | "sm"
  | "lg"
  | "xl"
  | "2xl"
  | "1x"
  | "2x"
  | "3x"
  | "4x"
  | "5x"
  | "6x"
  | "7x"
  | "8x"
  | "9x"
  | "10x"
>(() => {
  if (props.size === "large") {
    return "xl";
  }

  if (props.size === "medium") {
    return "lg";
  }

  return "1x";
});

const buttonClass = computed(() => {
  if (props.primary && !props.secondary) {
    return "icon-button--primary";
  }

  if (props.secondary) {
    return "icon-button--secondary";
  }

  return undefined;
});
</script>

<style scoped lang="scss">
.icon-button {
  border: 1px solid transparent;
  color: $color-text-brand;
  font-family: $font-family-default;
  gap: 0.375rem;
  padding: 0.375rem;
  @include body-small;

  @include desktop {
    height: 1.75rem;
    text-align: left;
  }
}

.icon-button--hide-label {
  :deep(.k-button__label) {
    @include sr-only;
  }
}

.icon-button--primary {
  background: transparent;
  border-color: transparent;

  &:hover,
  &:focus {
    background: $color-industry-blue-subtlest;
    border-color: $color-industry-blue-subtlest;
  }
}

.icon-button--disabled.icon-button--primary {
  color: $color-icon-button-disabled;

  &:hover,
  &:focus {
    background: transparent;
    border-color: transparent;
  }
}

.icon-button--secondary {
  background: $color-background-default;

  &:hover,
  &:focus {
    background: $color-industry-blue-highlight;
    border-color: $color-border-inverse;
  }
}

.icon-button--disabled.icon-button--secondary {
  background: $color-background-action-button-disabled;
  border-color: $color-border-subtlest;
  color: $color-icon-button-disabled;

  &:hover,
  &:focus {
    background: $color-background-action-button-disabled;
    border-color: $color-border-subtlest;
  }
}

.icon-button--medium {
  @include body;

  @include desktop {
    height: 2rem;
  }
}

.icon-button--large {
  @include body;
  gap: 0.5rem;

  @include desktop {
    height: 2.25rem;
  }
}

a.icon-button {
  cursor: pointer;
  text-decoration: none;
}
</style>
